import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Info, Trash } from "lucide-react";
import type { PatientFile } from "@/types/public";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { selectCurrentRoom } from "@/context/features/room/roomSlice";
import { shallowEqual, useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toaster } from "@/utils/toaster";
import { selectCurrentAuth } from "@/context/features/auth/authSlice";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { useRemoteParticipants } from "@/hooks/livekit/useRemoteParticipants";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Role } from "@/types/role.enum";
type Props = PatientFile & {
  onDownload?: (f: PatientFile) => void;
  onRemoveFile?: () => void;
};
const baseURL_docs =
  import.meta.env.VITE_BIOARC_SERVICE_DOCS || "https://bioarc.ir";

const toAbsUrl = (loc: string) => {
  try {
    return new URL(loc).toString();
  } catch {
    return `${baseURL_docs}/${loc}`;
  }
};

const getExt = (name?: string | null) =>
  (name || "").split("?")[0].split("#")[0].split(".").pop()?.toLowerCase() ||
  "";

const isImage = (ext: string) =>
  ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif"].includes(ext);
const isPDF = (ext: string) => ext === "pdf";
const isVideo = (ext: string) => ["mp4", "webm", "ogg", "mov"].includes(ext);
const isAudio = (ext: string) =>
  ["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(ext);
const origin = import.meta.env.VITE_BIOARC_SERVICE || "https://bioarc.ir";
const FileItem: React.FC<Props> = (props) => {
  const { filename, desc, location, onDownload, id, onRemoveFile, sender_id } =
    props;
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [latencyDelete, setLatencyDelete] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { t } = useTranslation();
  const { user } = useSelector(selectCurrentAuth);

  const { focusedhash, patientLine } = useSelector(
    selectCurrentRoom,
    shallowEqual
  );
  const { livekitContext } = useLivekitContext();
  const { room } = livekitContext ?? {};
  const remoteParticipants = useRemoteParticipants(room);
  const fileUrl = useMemo(() => {
    if (!id) return "";
    return toAbsUrl(`gvfile/ajaxLoadfile/${id}?hash=${focusedhash}`);
  }, [id, focusedhash]);

  const ext = useMemo(
    () => getExt(filename || location || undefined),
    [filename, location]
  );

  const handleOpenPreview = useCallback(() => {
    if (!location) return;
    setPreviewError(null);
    setLoadingPreview(true);
    setOpen(true);
  }, [location]);

  const downloadHandler = useCallback(async () => {
    if (onDownload) {
      onDownload(props);
      return;
    }
    if (!fileUrl) return;

    try {
      setDownloading(true);
      const res = await fetch(fileUrl, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        const a = document.createElement("a");
        a.href = fileUrl;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || "file";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      const a = document.createElement("a");
      a.href = fileUrl;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDownloading(false);
    }
  }, [onDownload, props, fileUrl, filename]);

  const deleteHandler = useCallback(async () => {
    if (!fileUrl) return;

    const url = `${origin}/VisitOnline/AjaxRemoveFile?id=${id}&hash=${focusedhash}`;
    setActionLoading(true);
    try {
      const res = await fetch(url, {
        credentials: "same-origin",
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
      }
      onRemoveFile?.();
    } catch (err) {
      console.error("Network/parse error:", err);
    } finally {
      setActionLoading(false);
      setLatencyDelete(true);
      toaster("info", t("file_deleted"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUrl, id]);

  const senderInfo = useMemo(() => {
    const fromRemote = remoteParticipants.find((p) => p.identity == sender_id);
    if (fromRemote) {
      const role = fromRemote.attributes.role;
      return {
        displayName: fromRemote.name ?? t("GUEST"),
        role: role ?? null,
      };
    }
    const fromPatientLine = patientLine?.find((p) => p.id === sender_id);
    if (fromPatientLine)
      return {
        displayName: fromPatientLine.fullName,
        role: fromPatientLine.role,
      };
    if (sender_id == user?.id)
      return { displayName: user.fullName, role: user.role };
    return { displayName: t("GUEST"), role: null };
  }, [
    sender_id,
    remoteParticipants,
    patientLine,
    user?.id,
    user?.role,
    user?.fullName,
    t,
  ]);

  const roleLabel = useMemo(() => {
    const r = senderInfo.role?.toUpperCase();
    if (r === Role.HOST) return t("host");
    if (r === Role.USER) return t("patient");
    if (r === Role.GUEST) return t("GUEST");
    return senderInfo.role ?? null;
  }, [senderInfo.role, t]);

  const senderName = useMemo(() => {
    return (
      <Popover>
        <PopoverTrigger asChild className="cursor-pointer px-1">
          <Button variant="ghost" className="w-8 h-8 p-1">
            <Info strokeWidth={1.5} className="h-5 w-5 text-foreground" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-fit text-foreground flex items-center gap-1 mx-2 p-2 px-3 mt-1">
          <span className="text-base">
            {roleLabel ? `${senderInfo.displayName}` : senderInfo.displayName}
          </span>
        </PopoverContent>
      </Popover>
    );
  }, [senderInfo.displayName, roleLabel]);
  useEffect(() => {
    setLatencyDelete(false);
  }, []);
  return latencyDelete ? (
    <div className="hidden"></div>
  ) : (
    <Fragment>
      <div
        className={cn(
          "p-1 ps-2 rounded-md flex items-center justify-between text-foreground shadow-sm",
          senderInfo.role === Role.USER ? "bg-[#FFF0DF] dark:bg-[#312B26]" : "",
          senderInfo.role === Role.HOST ? "bg-[#E8F6F1] dark:bg-[#1E3A32]" : ""
        )}
      >
        <div className="min-w-0 flex flex-col">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={"link"}
                size={"sm"}
                onClick={handleOpenPreview}
                className="text-left text-foreground truncate font-medium text-sm hover:underline focus:outline-none py-1"
              >
                {filename || ""}
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              className={cn(!desc ? "hidden" : "max-w-48")}
            >
              <p>{desc}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div>
          {actionLoading ? (
            <div className="w-8 h-8 p-2 flex items-center justify-center ">
              <div className="w-full h-full border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="flex gap-2 sm:gap-0">
              {sender_id == user?.id || user?.role === Role.HOST ? (
                <Fragment>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-8 h-8 p-1 text-foreground"
                        onClick={deleteHandler}
                        disabled={!location}
                      >
                        <Trash strokeWidth={1.5} className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>{t("delete")}</p>
                    </TooltipContent>
                  </Tooltip>
                  {user?.role === Role.HOST && <p>{senderName}</p>}
                </Fragment>
              ) : (
                senderName
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) {
            setLoadingPreview(true);
            setPreviewError(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="truncate text-wrap">
              {filename || t("file")}
            </DialogTitle>
            {desc ? (
              <DialogDescription className="truncate text-wrap max-h-36 md:max-h-52 overflow-auto">
                {desc}
              </DialogDescription>
            ) : null}
          </DialogHeader>

          <div className="mt-2">
            {!location ? (
              <div className="text-sm text-muted-foreground">
                {t("no_file_available")}
              </div>
            ) : (
              <div className="w-full h-[30vh] max-w-300 overflow-hidden">
                {loadingPreview && (
                  <Skeleton className="absolute inset-0 rounded-md" />
                )}

                {previewError && (
                  <div className="absolute inset-0 grid place-items-center p-4">
                    <div className="text-sm text-muted-foreground text-center space-y-2">
                      <p>
                        {t("preview_not_supported") ||
                          "Preview not supported or failed to load."}
                      </p>
                      <Button onClick={downloadHandler} size="sm">
                        {t("download")}
                      </Button>
                    </div>
                  </div>
                )}

                {isImage(ext) && !previewError && (
                  <img
                    src={fileUrl}
                    alt={filename || "image"}
                    className={`w-full h-full object-contain rounded-md border ${
                      loadingPreview ? "opacity-0" : "opacity-100"
                    } transition-opacity`}
                    loading="eager"
                    onLoad={() => setLoadingPreview(false)}
                    onError={() => {
                      setLoadingPreview(false);
                      setPreviewError("image");
                    }}
                  />
                )}

                {isPDF(ext) && !previewError && (
                  <iframe
                    src={fileUrl}
                    title={filename || "pdf"}
                    className={`w-full h-full rounded-md border ${
                      loadingPreview ? "opacity-0" : "opacity-100"
                    } transition-opacity`}
                    onLoad={() => setLoadingPreview(false)}
                  />
                )}

                {isVideo(ext) && !previewError && (
                  <video
                    src={fileUrl}
                    className={`w-full h-full rounded-md border ${
                      loadingPreview ? "opacity-0" : "opacity-100"
                    } transition-opacity`}
                    controls
                    onLoadedData={() => setLoadingPreview(false)}
                    onError={() => {
                      setLoadingPreview(false);
                      setPreviewError("video");
                    }}
                  />
                )}

                {isAudio(ext) && !previewError && (
                  <div className="w-full h-full rounded-md border grid place-items-center">
                    <audio
                      src={fileUrl}
                      controls
                      onLoadedData={() => setLoadingPreview(false)}
                      onError={() => {
                        setLoadingPreview(false);
                        setPreviewError("audio");
                      }}
                    />
                  </div>
                )}

                {!isImage(ext) &&
                  !isPDF(ext) &&
                  !isVideo(ext) &&
                  !isAudio(ext) &&
                  !previewError && (
                    <div className="p-4 rounded-md border bg-muted/30 text-sm">
                      {t("preview_not_supported") ||
                        "Preview not supported for this file type."}
                      <div className="mt-3">
                        <Button
                          onClick={downloadHandler}
                          size="sm"
                          variant="default"
                        >
                          {t("download")}
                        </Button>
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-row-reverse gap-2">
            <Button
              onClick={downloadHandler}
              disabled={downloading}
              className="flex-1 sm:flex-none"
            >
              {downloading ? t("downloading") : t("download")}
            </Button>
            <DialogClose asChild>
              <Button variant="outline" className="flex-1 sm:flex-none">
                {t("close")}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default React.memo(FileItem);
